import { useState } from "react";
import {
  Checkbox,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Select,
  Switch,
  InputLabel,
  IconButton,
} from "@mui/material";
import { Dp, ExplData, Action, ItemBuild } from "./CoState";
import { Filter } from "./CoQuery";
import CoUnits from "./CoUnits";
import CoStarUnits from "./CoStarUnits";
import CoTraits from "./CoTraits";
import CoItems from "./CoItems";
import CoBuilds from "./CoBuilds";
import CoIc from "./CoIc";
import type { ItemFilters } from "./CoItems";
import CoAugments from "./CoAugments";
import type { AugmentFilters } from "./CoAugments";
import CoPortals from "./CoPortals";
import CoCounters from "./CoCounters";
import CoBoards from "./CoBoards";
import { make as UnitImageExtended } from "common/src/tft-static/UnitImageExtended.bs";
import CloseIcon from "@mui/icons-material/Close";
import UnitImage from "common/src/tft-static/UnitImage";
import * as TraitImage from "common/src/tft-static/TraitImage";
import ItemImage from "common/src/tft-static/ItemImage";
import AugmentImage from "common/src/tft-static/AugmentImage";
import { humanReadableCount, formatPct2 } from "common/src/T.bs";
import { make as SearchField } from "../components/SearchField2.bs";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import * as T from "common/src/bindings/Translation2";
import { traitRankUnitCount } from "common/src/tft-static/Sd";
import { make as Tooltip } from "common/src/components/Tooltip.bs";
import { make as HorBarChart } from "../common/HorBarChart.bs";
import { make as DoubleHorBarChart } from "../common/DoubleHorBarChart.bs";
import { common as t } from "common/src/bindings/Translation2";
import {
  searchUnits,
  searchTraits,
  searchItems,
  searchAugments,
} from "../common/Search2";
import { use as useSet } from "common/src/data/TftSet.bs";
import { generateBoard as generateBoard11 } from "../team-planner11/TbUtils11.bs";

type tab =
  | "Units"
  | "Traits"
  | "Items"
  | "Builds"
  | "IC"
  | "Augments"
  | "Portals"
  | "Boards"
  | "Counters"
  | "Headliners";

type TableProps = {
  tab: tab;
  data: ExplData;
  onExpand: (f: Filter) => void;
  onSetBuild: (f: ItemBuild) => void;
  search: string;
  itemFilters: ItemFilters;
  augmentFilters: AugmentFilters;
  splitStarLevel: boolean;
  splitHolder: boolean;
  minSample: number;
  hasUnitFilters: boolean;
  exaltedGroups: Array<Array<string>>;
  dispatch: any;
};

const getTable = ({
  tab,
  data,
  onExpand,
  onSetBuild,
  dispatch,
  search,
  itemFilters,
  augmentFilters,
  splitStarLevel,
  splitHolder,
  minSample,
  hasUnitFilters,
  exaltedGroups,
}: TableProps) => {
  switch (tab) {
    case "Units":
      if (splitStarLevel) {
        return (
          <CoStarUnits
            data={data.starUnits}
            baseCount={data.base.count}
            onExpand={onExpand}
            search={search}
            hideExpand={false}
            minSample={minSample}
          />
        );
      } else {
        return (
          <CoUnits
            data={data.units}
            baseCount={data.base.count}
            onExpand={onExpand}
            search={search}
            hideExpand={false}
            minSample={minSample}
          />
        );
      }
    case "Traits":
      return (
        <CoTraits
          data={data.traits}
          baseCount={data.base.count}
          onExpand={onExpand}
          search={search}
          hideExpand={false}
          minSample={minSample}
        />
      );
    case "Items":
      return (
        <CoItems
          itemFilters={itemFilters}
          baseItems={data.baseItems}
          unitItems={data.unitItems}
          baseCount={data.base.count}
          onExpand={onExpand}
          search={search}
          hideExpand={false}
          splitHolder={splitHolder}
          minSample={minSample}
          hasUnitFilters={hasUnitFilters}
        />
      );
    case "Builds":
      return (
        <CoBuilds
          itemFilters={itemFilters}
          builds={data.itemBuilds}
          baseCount={data.base.count}
          onExpand={onSetBuild}
          search={search}
          hideExpand={false}
          minSample={minSample}
          hasUnitFilters={hasUnitFilters}
        />
      );
    case "IC":
      return (
        <CoIc
          search={search}
          minSample={minSample}
          data={data.unitItemCounts}
        />
      );
    case "Augments":
      return (
        <CoAugments
          augmentFilters={augmentFilters}
          data={data.augments}
          baseCount={data.base.count}
          onExpand={onExpand}
          search={search}
          hideExpand={false}
          minSample={minSample}
        />
      );
    case "Portals":
      return (
        <CoPortals
          data={data.portals}
          baseCount={data.base.count}
          onExpand={onExpand}
          search={search}
          hideExpand={false}
          minSample={minSample}
        />
      );
    case "Counters":
      return (
        <CoCounters
          data={data.oppUnits}
          baseCount={data.base.count}
          // onExpand={onExpand}
          search={search}
          // hideExpand={false}
          minSample={minSample}
        />
      );
    case "Boards":
      return <CoBoards matches={data.matches} search={search} />;
  }
};

type TableControlProps = {
  tab: tab;
  augmentFilters: AugmentFilters;
  setAugmentFilters: (v: (ifs: AugmentFilters) => AugmentFilters) => void;
  itemFilters: ItemFilters;
  setItemFilters: (v: (ifs: ItemFilters) => ItemFilters) => void;
  splitHolder: boolean;
  setSplitHolder: (v: boolean) => void;
  splitStarLevel: boolean;
  setSplitStarLevel: (v: boolean) => void;
};

const renderFilter = (f: Filter) => {
  switch (f.typ) {
    case "u": {
      if (f.starLevel === 0) {
        return <UnitImage unitId={f.id} size={32} />;
      } else {
        return (
          <UnitImageExtended
            unitId={f.id}
            size={32}
            starLevel={f.starLevel}
            items={[]}
            forceStarLevel
          />
        );
      }
    }
    case "t":
      const adjRank = Number(f.rank.replace(/\+|-/, ""));
      let suffix = "";
      if (f.rank[0] === "+") {
        suffix = "+";
      } else if (f.rank[0] === "-") {
        suffix = "-";
      }
      if (adjRank === 0) {
        return <TraitImage.PlainIcon traitId={f.id} size={32} />;
      } else {
        return (
          <TraitImage.PlainIcon2
            traitId={f.id}
            rank={adjRank}
            size={32}
            suffix={suffix}
          />
        );
      }
    case "i":
      return (
        <div className="flex relative">
          <ItemImage itemId={String(f.id)} size={30} rounded="rounded" />
          {f.unitId && (
            <div className="absolute right-[-2px] bottom-[-4px]">
              <UnitImage unitId={f.unitId[0]} size={18} showName={false} />
            </div>
          )}
        </div>
      );
    case "a":
      return <AugmentImage augId={String(f.id)} size={32} />;
  }
};

type DataTableProps = {
  queryData: undefined | [Array<Filter>, Array<Filter>, ExplData | undefined];
  onExpand: (f: Filter) => void;
  onSetBuild: (f: ItemBuild) => void;
  dispatch: (a: Action) => void;
  screenSize: number;
  exaltedGroups: Array<Array<string>>;
};

type IfsProps = {
  itemFilters: ItemFilters;
  setItemFilters: (v: (ifs: ItemFilters) => ItemFilters) => void;
};
const ItemFiltersSelect = ({ itemFilters, setItemFilters }: IfsProps) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const filtersEnabled =
    !(
      itemFilters.craftable &&
      itemFilters.ornn &&
      itemFilters.emblem &&
      itemFilters.radiant &&
      itemFilters.support &&
      itemFilters.other
    ) &&
    (itemFilters.craftable ||
      itemFilters.ornn ||
      itemFilters.emblem ||
      itemFilters.radiant ||
      itemFilters.support ||
      itemFilters.other);

  return (
    <>
      <div
        className="mr-4 cursor-pointer"
        onClick={(e) => setMenuAnchor(e.currentTarget)}
      >
        <FilterAltIcon color={filtersEnabled ? "primary" : undefined} />
      </div>
      <Menu
        open={menuAnchor !== null}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() =>
            setItemFilters((vs) => ({ ...vs, craftable: !vs.craftable }))
          }
          classes={{
            root: "pl-1 py-[2px]",
          }}
        >
          <Checkbox size="small" checked={itemFilters.craftable} />
          {t("Craftable")}
        </MenuItem>
        <MenuItem
          onClick={() => setItemFilters((vs) => ({ ...vs, ornn: !vs.ornn }))}
          classes={{
            root: "pl-1 py-[2px]",
          }}
        >
          <Checkbox size="small" checked={itemFilters.ornn} />
          {t("Ornn")}
        </MenuItem>
        <MenuItem
          onClick={() =>
            setItemFilters((vs) => ({ ...vs, emblem: !vs.emblem }))
          }
          classes={{
            root: "pl-1 py-[2px]",
          }}
        >
          <Checkbox size="small" checked={itemFilters.emblem} />
          {t("Emblem")}
        </MenuItem>
        <MenuItem
          onClick={() =>
            setItemFilters((vs) => ({ ...vs, radiant: !vs.radiant }))
          }
          classes={{
            root: "pl-1 py-[2px]",
          }}
        >
          <Checkbox size="small" checked={itemFilters.radiant} />
          {t("Radiant")}
        </MenuItem>
        <MenuItem
          onClick={() =>
            setItemFilters((vs) => ({ ...vs, support: !vs.support }))
          }
          classes={{
            root: "pl-1 py-[2px]",
          }}
        >
          <Checkbox size="small" checked={itemFilters.support} />
          {t("Support")}
        </MenuItem>
        <MenuItem
          onClick={() => setItemFilters((vs) => ({ ...vs, other: !vs.other }))}
          classes={{
            root: "pl-1 py-[2px]",
          }}
        >
          <Checkbox size="small" checked={itemFilters.other} />
          {t("Other")}
        </MenuItem>
      </Menu>
    </>
  );
};

type AfsProps = {
  augmentFilters: AugmentFilters;
  setAugmentFilters: (v: (ifs: AugmentFilters) => AugmentFilters) => void;
};
const AugmentFiltersSelect = ({
  augmentFilters,
  setAugmentFilters,
}: AfsProps) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const filtersEnabled =
    !(
      augmentFilters.silvers &&
      augmentFilters.golds &&
      augmentFilters.prisms
    ) &&
    (augmentFilters.silvers || augmentFilters.golds || augmentFilters.prisms);

  return (
    <>
      <Menu
        open={menuAnchor !== null}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() =>
            setAugmentFilters((vs) => ({ ...vs, silvers: !vs.silvers }))
          }
          classes={{
            root: "pl-1 py-[2px]",
          }}
        >
          <Checkbox size="small" checked={augmentFilters.silvers} />
          {t("Silver")}
        </MenuItem>
        <MenuItem
          onClick={() =>
            setAugmentFilters((vs) => ({ ...vs, golds: !vs.golds }))
          }
          classes={{
            root: "pl-1 py-[2px]",
          }}
        >
          <Checkbox size="small" checked={augmentFilters.golds} />
          {t("Gold")}
        </MenuItem>
        <MenuItem
          onClick={() =>
            setAugmentFilters((vs) => ({ ...vs, prisms: !vs.prisms }))
          }
          classes={{
            root: "pl-1 py-[2px]",
          }}
        >
          <Checkbox size="small" checked={augmentFilters.prisms} />
          {t("Prismatic")}
        </MenuItem>
      </Menu>
      <div
        className="mr-4 cursor-pointer"
        onClick={(e) => setMenuAnchor(e.currentTarget)}
      >
        <FilterAltIcon color={filtersEnabled ? "primary" : undefined} />
      </div>
    </>
  );
};

const getTableControls = ({
  tab,
  itemFilters,
  setItemFilters,
  augmentFilters,
  setAugmentFilters,
  splitStarLevel,
  splitHolder,
  setSplitHolder,
  setSplitStarLevel,
}: TableControlProps) => {
  switch (tab) {
    case "Units":
      return (
        <>
          <Switch
            id="star-switch"
            checked={splitStarLevel}
            onChange={(e) => setSplitStarLevel(e.target.checked)}
            size="small"
          />
          <InputLabel htmlFor="star-switch">
            {t("Split by star level")}
          </InputLabel>
        </>
      );
    case "Traits":
      return null;
    case "Items":
      return (
        <>
          <Switch
            id="holder-switch"
            checked={splitHolder}
            onChange={(e) => setSplitHolder(e.target.checked)}
            size="small"
          />
          <InputLabel htmlFor="holder-switch">
            {t("Split by item holder")}
          </InputLabel>
          <div className="ml-auto">
            <ItemFiltersSelect
              itemFilters={itemFilters}
              setItemFilters={setItemFilters}
            />
          </div>
        </>
      );
    case "Builds":
      return (
        <div className="ml-auto">
          <ItemFiltersSelect
            itemFilters={itemFilters}
            setItemFilters={setItemFilters}
          />
        </div>
      );
    case "Augments":
      return (
        <div className="ml-auto">
          <AugmentFiltersSelect
            augmentFilters={augmentFilters}
            setAugmentFilters={setAugmentFilters}
          />
        </div>
      );
    case "Portals":
      return null;
    case "Counters":
      return null;
    case "Boards":
      return null;
  }
};

const download = (filename: string, text: string) => {
  var element = document.createElement("a");
  element.setAttribute(
    "href",
    "data:text/csv;charset=utf-8," + encodeURIComponent(text),
  );
  element.setAttribute("download", filename);

  element.style.display = "none";
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
};

const formatDp = (dp: Dp) =>
  `${dp.count},${(dp.place / dp.count).toFixed(2)},${dp.delta.toFixed(2)},${(
    (100 * dp.top4) /
    dp.count
  ).toFixed(2)}%,${((100 * dp.won) / dp.count).toFixed(2)}%`;

const downloadCsv = (
  tab: tab,
  data: ExplData,
  split: boolean,
  search: string,
  set: number,
) => {
  let contents = "";
  if (tab === "Units") {
    if (split) {
      let starUnitsData;
      if (search === "") {
        starUnitsData = data.starUnits;
      } else {
        const passIds = searchUnits(search, set);
        starUnitsData = data.starUnits.filter(([unitId]) =>
          passIds.has(unitId),
        );
      }
      contents =
        `Unit,Games,Place,Delta,Top4,Won\n` +
        starUnitsData
          .map(
            ([unitId, starLevel, dp]) =>
              `${starLevel}* ${T.units(unitId)},${formatDp(dp)}`,
          )
          .join("\n");
    } else {
      let unitsData;
      if (search === "") {
        unitsData = data.units;
      } else {
        const passIds = searchUnits(search, set);
        unitsData = data.units.filter(([unitId]) => passIds.has(unitId));
      }
      contents =
        `Unit,Games,Place,Delta,Top4,Won\n` +
        unitsData
          .map(([unitId, dp]) => `${T.units(unitId)},${formatDp(dp)}`)
          .join("\n");
    }
  } else if (tab === "Traits") {
    let traitsData;
    if (search === "") {
      traitsData = data.traits;
    } else {
      const passIds = searchTraits(search, set);
      traitsData = data.traits.filter(([traitId]) => passIds.has(traitId));
    }
    contents =
      `Trait,Games,Place,Delta,Top4,Won\n` +
      traitsData
        .map(([traitId, rank, dp]) => {
          const count = traitRankUnitCount(traitId, rank, 80);
          return `${count} ${T.traits(traitId)},${formatDp(dp)}`;
        })
        .join("\n");
  } else if (tab === "Items") {
    if (split) {
      let itemsData;
      if (search === "") {
        itemsData = data.unitItems;
      } else {
        const passIds = searchItems(search, set);
        const passUnitIds = searchUnits(search, set);
        itemsData = data.unitItems.filter(
          ([unitId, _1, itemId]) =>
            passUnitIds.has(unitId) || passIds.has(itemId),
        );
      }

      contents =
        `Unit,Item,Games,Place,Delta,Top4,Won\n` +
        itemsData
          .map(
            ([unitId, _sl, itemId, dp]) =>
              `${T.units(unitId)},${T.items(String(itemId))},${formatDp(dp)}`,
          )
          .join("\n");
    } else {
      let itemsData;
      if (search === "") {
        itemsData = data.baseItems;
      } else {
        const passIds = searchItems(search, set);
        itemsData = data.baseItems.filter(([itemId]) => passIds.has(itemId));
      }
      contents =
        `Item,Games,Place,Delta,Top4,Won\n` +
        itemsData
          .map(([itemId, dp]) => `${T.items(String(itemId))},${formatDp(dp)}`)
          .join("\n");
    }
  } else if (tab === "Augments") {
    let augsData;
    if (search === "") {
      augsData = data.augments;
    } else {
      const passIds = searchAugments(search, set);
      augsData = data.augments.filter(([augId]) => passIds.has(augId));
    }

    contents =
      `Augment,Games,Place,Delta,Top4,Won,At 2-1,At 3-2,At 4-2\n` +
      augsData
        .map(
          ([augId, dp, dp21, dp32, dp42]) =>
            `${T.augments(String(augId))},${formatDp(dp)},${
              dp21 ? (dp21.place / dp21.count).toFixed(2) : "-"
            },${dp32 ? (dp32.place / dp32.count).toFixed(2) : "-"},${
              dp42 ? (dp42.place / dp42.count).toFixed(2) : "-"
            }`,
        )
        .join("\n");
  }
  download("data.csv", contents);
};

const DataTable = ({
  screenSize,
  queryData,
  onExpand,
  onSetBuild,
  dispatch,
  exaltedGroups,
}: DataTableProps) => {
  const set = useSet();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [tab, setTab] = useState<tab>("Units");
  const [search, setSearch] = useState("");
  const [minSample, setMinSample] = useState(100);

  const [itemFilters, setItemFilters] = useState({
    craftable: false,
    ornn: false,
    emblem: false,
    radiant: false,
    support: false,
    other: false,
  });
  const [augmentFilters, setAugmentFilters] = useState({
    silvers: false,
    golds: false,
    prisms: false,
  });

  const [splitStarLevel, setSplitStarLevel] = useState(false);

  const [splitHolder, setSplitHolder] = useState(true);

  if (queryData == null) {
    return (
      <div className="text-xl font-montserrat font-semibold text-white2 p-4">
        No Data
      </div>
    );
  } else {
    const [filters, excludeFilters, data] = queryData;
    if (filters.length === 0 && data == null) {
      return (
        <div className="p-4 flex flex-col items-start gap-2 text-base">
          <div className="text-xl">Explore Stats By Adding Filters</div>
        </div>
      );
    }

    const hasUnitFilters = filters.some((f) => f.typ === "u");

    return (
      <div>
        <div className="p-3 pb-0 flex flex-col sticky top-[47px] md:top-[92px] bg-bg0 z-[100]">
          <div className="flex flex-row gap-4">
            <div className="flex flex-col md:flex-row w-full justify-between">
              <div className="flex gap-4">
                <div className="flex items-center gap-[10px]">
                  {filters.map((f, idx) => {
                    const el = renderFilter(f);
                    return (
                      <div key={idx} className="relative">
                        {el}
                        <div
                          className="absolute left-0 top-0 right-0 bottom-0 rounded opacity-0 hover:opacity-100 bg-[#44444488] cursor-pointer z-10 flex items-center justify-center transition"
                          onClick={(_) =>
                            dispatch(["set_filter", [idx, undefined]])
                          }
                        >
                          <CloseIcon fontSize="small" />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="pl-2 flex items-center gap-[10px]">
                  {excludeFilters.length > 0 ? (
                    <div className="w-3">—</div>
                  ) : null}
                  {excludeFilters.map((f, idx) => {
                    const el = renderFilter(f);
                    return (
                      <div key={idx} className="relative">
                        {el}
                        <div
                          className="absolute left-0 top-0 right-0 bottom-0 rounded opacity-0 hover:opacity-100 bg-[#44444488] cursor-pointer z-10 flex items-center justify-center transition"
                          onClick={(_) =>
                            dispatch(["set_exclude_filter", [idx, undefined]])
                          }
                        >
                          <CloseIcon fontSize="small" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex self-end md:self-start items-center">
                <div className="text-sm pr-[6px]">
                  {t("Minimum sample size")}
                </div>
                <Select
                  value={minSample}
                  label="Minimum sample size"
                  onChange={(e) => setMinSample(Number(e.target.value))}
                  variant="standard"
                  sx={{ width: 46 }}
                  classes={{
                    select: "!pr-[18px] py-[3px] text-[15px]",
                    icon: "my-[2px] w-[20px] h-[20px]",
                  }}
                >
                  <MenuItem value={10000}>10k</MenuItem>
                  <MenuItem value={3000}>3k</MenuItem>
                  <MenuItem value={1000}>1k</MenuItem>
                  <MenuItem value={300}>300</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                </Select>
                <div>
                  <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchor}
                    open={menuAnchor != null}
                    onClose={() => setMenuAnchor(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        let split = false;
                        if (tab === "Units") {
                          split = splitStarLevel;
                        } else if (tab === "Traits") {
                        } else if (tab === "Items") {
                          split = splitHolder;
                        } else if (tab === "Augments") {
                        }

                        if (data) {
                          downloadCsv(tab, data, split, search, set);
                        } else {
                          alert("No Data");
                        }
                        setMenuAnchor(null);
                      }}
                    >
                      {T.common("Download CSV")}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        const units = filters
                          .filter((f) => f.typ === "u")
                          .map((f) => f.sid);
                        const board = generateBoard11(units);
                        window.open(`/team-builder/${board}`, "_blank");

                        setMenuAnchor(null);
                      }}
                    >
                      {T.common("Open in Team Builder")}
                    </MenuItem>
                  </Menu>
                </div>
              </div>
            </div>
          </div>
          {data && (
            <div className="pt-[10px] flex gap-4 text-sm sm:text-base">
              <div className="text-white2">
                {t("Games")}:{" "}
                <span className="font-medium text-white1">
                  {humanReadableCount(data.base.count)}
                </span>
              </div>
              {
                <>
                  <Tooltip
                    title={
                      <div>
                        <div className="pb-2">Placement distribution</div>
                        <HorBarChart
                          data={data.plDist.map((v, idx) => [
                            String(idx + 1),
                            v,
                          ])}
                        />
                      </div>
                    }
                  >
                    <div className="text-white2">
                      {t("Avg.")}:{" "}
                      <span className="font-medium text-white1">
                        {(data.base.place / data.base.count).toFixed(2)}
                      </span>
                    </div>
                  </Tooltip>
                  <div className="text-white2">
                    {t("Top 4")}:{" "}
                    <span className="font-medium text-white1">
                      {formatPct2(data.base.top4 / data.base.count)}
                    </span>
                  </div>
                  <div className="text-white2">
                    {t("Win")}:{" "}
                    <span className="font-medium text-white1">
                      {formatPct2(data.base.won / data.base.count)}
                    </span>
                  </div>
                  <Tooltip
                    title={
                      <div>
                        <div className="pb-2">Level distribution</div>
                        <DoubleHorBarChart
                          wideLabel
                          data={data.levels
                            .filter(([level, _]) => level > 6)
                            .map(([level, dp]) => [
                              String(level),
                              dp.count / data.base.count,
                              dp.place / dp.count,
                            ])}
                        />
                      </div>
                    }
                  >
                    <div className="text-white2">
                      {t("Avg Level")}:{" "}
                      <span className="font-medium text-white1">
                        {(data.level / data.base.count).toFixed(2)}
                      </span>
                    </div>
                  </Tooltip>
                </>
              }
            </div>
          )}
          {data ? (
            <>
              <div className="p-2 flex items-center">
                <div className="pr-5">
                  <SearchField search={search} setSearch={setSearch} />
                </div>
                {getTableControls({
                  tab,
                  itemFilters,
                  setItemFilters,
                  augmentFilters,
                  setAugmentFilters,
                  splitStarLevel,
                  splitHolder,
                  setSplitHolder,
                  setSplitStarLevel,
                })}
              </div>
              <div className="mx-[-12px] pt-2 pb-0">
                <Tabs
                  variant={"scrollable"}
                  value={tab}
                  onChange={(_: any, t: tab) => {
                    setTab(t);
                    setSearch("");
                  }}
                >
                  <Tab label={t("Units")} value="Units" />
                  <Tab label={t("Traits")} value="Traits" />
                  <Tab label={t("Items")} value="Items" />
                  <Tab label={t("Builds")} value="Builds" />
                  {/* <Tab label={t("Augments")} value="Augments" /> */}
                  <Tab label={t("Item Count Tab")} value="IC" />
                  <Tab label={t("Counters")} value="Counters" />
                  <Tab label={t("Boards")} value="Boards" />
                </Tabs>
              </div>
            </>
          ) : null}
        </div>
        {data ? (
          <>
            {getTable({
              minSample,
              tab,
              data,
              onExpand,
              onSetBuild,
              dispatch,
              search,
              itemFilters,
              augmentFilters,
              splitStarLevel,
              splitHolder,
              hasUnitFilters,
              exaltedGroups,
            })}
          </>
        ) : (
          <div className="text-xl font-montserrat font-semibold text-white2 p-4">
            No Data
          </div>
        )}
      </div>
    );
  }
};

export default DataTable;
